import { useState } from 'react';
import { BatchAssignmentPlanner } from '@/features/management-dashboard/components/BatchAssignmentPlanner';
import { ClientExplorerSection } from '@/features/management-dashboard/components/ClientExplorerSection';
import { ManagementDashboardTabs } from '@/features/management-dashboard/components/ManagementDashboardTabs';
import { UserExplorerSection } from '@/features/management-dashboard/components/UserExplorerSection';
import { useManagementDashboardData } from '@/features/management-dashboard/useManagementDashboardData';
import { OverviewDashboardSection } from '@/features/management-dashboard/components/OverviewDashboardSection';
import { ProjectReportSection } from '@/features/reports/components/ProjectReportSection';
import { exportReportForWeb } from '@/features/reports/api';

export function ManagementDashboardReportTab() {
  const {
    currentUser,
    loading,
    error, clearError,
    range,
    setRange,
    rangeOptions,
    activeTab,
    setActiveTab,
    kpis,
    charts,
    users,
    clients,
    tickets,
    fms,
    todo,
    invoices,
    attendance,
    selectedUserId,
    setSelectedUserId,
    selectedUser,
    userExplorer,
    selectedClientId,
    setSelectedClientId,
    selectedClient,
    clientExplorer,
    clientTicketStats,
    selectedProject,
    setSelectedProject,
    refresh,
    bounds,
    startDate,
    endDate,
    customStart,
    setCustomStart,
    customEnd,
    setCustomEnd
  } = useManagementDashboardData();

  const [exportingFormat, setExportingFormat] = useState(null);
  const [exportMsg, setExportMsg] = useState(null);

  const handleExport = async (format) => {
    setExportingFormat(format);
    setExportMsg(null);
    try {
      let sheetName = 'Management_Report';
      let filters = { scope: 'management' };

      if (activeTab === 'project-report') {
        sheetName = 'Project_Report';
        filters = { scope: 'project', selectedProject };
      } else if (activeTab === 'user-explorer') {
        sheetName = 'User_Explorer_Report';
        filters = { scope: 'user-explorer', userId: selectedUserId, userName: selectedUser?.['Employee Name'] || selectedUser?.name };
      } else if (activeTab === 'client-explorer') {
        sheetName = 'Client_Explorer_Report';
        filters = { scope: 'client-explorer', clientId: selectedClientId, clientName: selectedClient?.['Client Name'] || selectedClient?.name };
      }

      const employeeId = currentUser?.['Employee ID'] || '';
      const role = currentUser?.Role || 'Admin';
      const activeStart = bounds?.startDate || startDate || customStart || '';
      const activeEnd = bounds?.endDate || endDate || customEnd || '';

      const payload = await exportReportForWeb(
        format,
        sheetName,
        employeeId,
        role,
        activeStart,
        activeEnd,
        filters
      );

      if (!payload?.success || !payload?.base64Data) {
        throw new Error(payload?.message || 'Report export failed.');
      }

      if (typeof window !== 'undefined') {
        const binaryString = window.atob(payload.base64Data);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const mimeType = format === 'pdf' ? 'application/pdf' : format === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv';
        const blob = new Blob([bytes], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = payload.fileName || `management-report.${format}`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 100);
      }

      setExportMsg({
        tone: 'success',
        text: `${sheetName.replace(/_/g, ' ')} exported successfully (${format.toUpperCase()}).`
      });
    } catch (err) {
      setExportMsg({ tone: 'danger', text: err.message || 'Export failed.' });
    } finally {
      setExportingFormat(null);
    }
  };

  return (
    <>
      {error ? <div className="dashboard-banner dashboard-banner--error"><span>{error}</span><button type="button" className="dashboard-banner__close" onClick={clearError}>OK</button></div> : null}
      {loading ? <div className="dashboard-banner"><span>Loading report data from MongoDB...</span></div> : null}
      {exportMsg ? (
        <div className={`dashboard-banner${exportMsg.tone === 'danger' ? ' dashboard-banner--error' : ''}`} style={{ marginBottom: '12px' }}>
          <span>{exportMsg.text}</span>
          <button type="button" className="dashboard-banner__close" onClick={() => setExportMsg(null)}>OK</button>
        </div>
      ) : null}

      <ManagementDashboardTabs activeTab={activeTab} onTabChange={setActiveTab}>
        <div className="reports-toolbar__actions" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {activeTab !== 'batch-planner' && activeTab !== 'project-report' && (
            <>
              {range === 'custom' && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <label className="dashboard-control" style={{ margin: 0 }}>
                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>From</span>
                    <input
                      type="date"
                      value={customStart}
                      onChange={(event) => setCustomStart(event.target.value)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        background: '#ffffff',
                        outline: 'none',
                        color: '#1e293b'
                      }}
                    />
                  </label>
                  <label className="dashboard-control" style={{ margin: 0 }}>
                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>To</span>
                    <input
                      type="date"
                      value={customEnd}
                      onChange={(event) => setCustomEnd(event.target.value)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        background: '#ffffff',
                        outline: 'none',
                        color: '#1e293b'
                      }}
                    />
                  </label>
                </div>
              )}
              <label className="dashboard-control" style={{ margin: 0, marginRight: '12px' }}>
                <span>Date Range</span>
                <select value={range} onChange={(event) => setRange(event.target.value)}>
                  {rangeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
          {activeTab !== 'batch-planner' && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginLeft: 'auto' }}>
              <button
                type="button"
                className="attendance-cta attendance-cta--red"
                disabled={Boolean(exportingFormat)}
                onClick={() => handleExport('pdf')}
                style={{ padding: '0 16px', height: '36px', fontSize: '13px' }}
              >
                {exportingFormat === 'pdf' ? 'Exporting...' : 'PDF'}
              </button>
              <button
                type="button"
                className="attendance-cta attendance-cta--green"
                disabled={Boolean(exportingFormat)}
                onClick={() => handleExport('xlsx')}
                style={{ padding: '0 16px', height: '36px', fontSize: '13px' }}
              >
                {exportingFormat === 'xlsx' ? 'Exporting...' : 'Excel'}
              </button>
            </div>
          )}
        </div>
      </ManagementDashboardTabs>

      {activeTab !== 'batch-planner' ? (
        <div className="management-dashboard-content-zoom" style={{ zoom: 1.1, width: '100%' }}>
          {activeTab === 'overview' ? (
            <OverviewDashboardSection
              kpis={kpis}
              charts={charts}
              tickets={tickets}
              fms={fms}
              todo={todo}
              users={users}
              clients={clients}
              invoices={invoices}
              attendance={attendance}
              onOpenBatchModal={(type) => setActiveTab('batch-planner')}
            />
          ) : null}

          {activeTab === 'user-explorer' ? (
            <UserExplorerSection
              users={users}
              selectedUserId={selectedUserId}
              onUserChange={setSelectedUserId}
              selectedUser={selectedUser}
              explorer={userExplorer}
            />
          ) : null}

          {activeTab === 'client-explorer' ? (
            <ClientExplorerSection
              clients={clients}
              selectedClientId={selectedClientId}
              onClientChange={setSelectedClientId}
              selectedClient={selectedClient}
              explorer={clientExplorer}
              tickets={tickets}
              fms={fms}
              todo={todo}
              invoices={invoices}
              clientTicketStats={clientTicketStats}
              currentUser={currentUser}
            />
          ) : null}

          {activeTab === 'project-report' ? (
            <ProjectReportSection
              tickets={tickets}
              selectedProject={selectedProject}
              onProjectChange={setSelectedProject}
              customStart={customStart}
              onCustomStartChange={setCustomStart}
              customEnd={customEnd}
              onCustomEndChange={setCustomEnd}
            />
          ) : null}
        </div>
      ) : null}

      {activeTab === 'batch-planner' ? (
        <BatchAssignmentPlanner
          users={users}
          clients={clients}
          currentUser={currentUser}
          onCompleted={refresh}
        />
      ) : null}
    </>
  );
}
